/* 707dot: one-second ease-in fade to black, then reveal the new document. */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const STORAGE_KEY = 'sevenPageTransition';
  let navigating = false;
  let navigationTimer = 0;
  let recoveryTimer = 0;
  let arrivalTimer = 0;
  let arrivalCycle = 0;

  const contactDestination = (url) => {
    // The existing Contact menu uses the store's former domain. Keep that
    // page in this storefront (and in the currently selected preview theme).
    if (['http:', 'https:'].includes(url.protocol)
      && ['707bit.com', 'www.707bit.com', '707dot.com', 'www.707dot.com'].includes(url.hostname)
      && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/contact-us\/?$/i.test(url.pathname)) {
      return new URL(url.pathname + url.search + url.hash, window.location.origin);
    }
    return url;
  };

  const enterHeader = () => {
    root.classList.remove('header-page-pending');
    const header = document.querySelector('.section-header');
    if (!header || reduceMotion.matches || designMode) return;
    // Reuse the first Intro's staggered UI entrance; the Logo stays in place.
    header.classList.remove('header-entering', 'header-ready', 'header-page-entering');
    void header.offsetWidth;
    header.classList.add('header-entering');
  };

  const revealPage = async () => {
    const cycle = ++arrivalCycle;
    const firstIntro = root.classList.contains('intro-sequence-pending');
    // The first Intro owns the actual Header logo even while video is loading.
    if (firstIntro) root.classList.remove('header-page-pending');
    // Wait behind the document's black layer until the hero has a real frame.
    // A first Intro supplies its own black layer and waits before its handoff.
    if (!firstIntro && window.__sevenHomeVideoReady) await window.__sevenHomeVideoReady;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (navigating || cycle !== arrivalCycle) return;
      root.classList.remove('page-transition-arriving');
      if (!root.classList.contains('intro-sequence-pending')) enterHeader();
      else root.classList.remove('header-page-pending');
      arrivalTimer = window.setTimeout(() => {
        document.dispatchEvent(new CustomEvent('sevenzeroseven:page-arrived'));
      }, 1000);
    }));
  };

  const clearMarker = () => {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (error) {}
  };

  const reset = () => {
    arrivalCycle += 1;
    window.clearTimeout(navigationTimer);
    window.clearTimeout(recoveryTimer);
    window.clearTimeout(arrivalTimer);
    navigating = false;
    root.classList.add('page-transition-reset');
    root.classList.remove('page-transition-arriving', 'page-transition-leaving');
    requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('page-transition-reset')));
    clearMarker();
  };

  const setupPolicySpacing = () => {
    if (!document.querySelector('#MainContent > .shopify-policy__container')) return;
    const header = document.querySelector('.section-header');
    if (!header) return;
    let viewportWidth = window.innerWidth;
    let reservedHeight = 0;
    const update = () => {
      const height = Math.ceil(header.offsetHeight);
      if (viewportWidth !== window.innerWidth) {
        viewportWidth = window.innerWidth;
        reservedHeight = height;
      } else {
        // Scrolling may shrink the Logo; keep content stable while reading.
        reservedHeight = Math.max(reservedHeight, height);
      }
      root.style.setProperty('--policy-header-height', `${reservedHeight}px`);
    };
    update();
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(header);
    window.addEventListener('resize', update, { passive: true });
    document.fonts?.ready.then(update).catch(() => {});
  };

  const initialize = () => {
    setupPolicySpacing();
    document.querySelectorAll('a[href]').forEach((link) => {
      try {
        const original = new URL(link.href, window.location.href);
        const resolved = contactDestination(original);
        if (resolved.href !== original.href) link.href = resolved.href;
      } catch (error) {}
    });
    if (designMode || reduceMotion.matches) {
      root.classList.remove('header-page-pending');
      reset();
      return;
    }
    root.classList.add('page-transition-enabled');
    revealPage();

    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || reduceMotion.matches) return;
      const link = event.target.closest?.('a[href]');
      if (!link || link.hasAttribute('download') || link.hasAttribute('data-no-page-transition') || link.getAttribute('aria-disabled') === 'true' || link.getAttribute('role') === 'button') return;
      if (link.target && link.target.toLowerCase() !== '_self') return;
      let destination;
      try { destination = contactDestination(new URL(link.href, window.location.href)); } catch (error) { return; }
      if (!['http:', 'https:'].includes(destination.protocol) || destination.origin !== window.location.origin) return;
      if (destination.pathname === window.location.pathname && destination.search === window.location.search) return;
      // Purchase, authentication, app actions and file transfers retain native behavior.
      if (/\/(?:checkout|checkouts|account|customer_authentication|localization|apps|tools)(?:\/|$)/i.test(destination.pathname) || /\/cart\/(?:add|change|update|clear)(?:\.|\/|$)/i.test(destination.pathname) || /\.(?:pdf|zip|png|jpe?g|webp|svg|mp4)(?:$)/i.test(destination.pathname)) return;

      event.preventDefault();
      if (navigating) return;
      navigating = true;
      root.classList.remove('page-transition-reset', 'page-transition-arriving');
      root.classList.add('page-transition-leaving');
      // If a browser/network fails to leave, restore the usable source page.
      recoveryTimer = window.setTimeout(reset, 12000);
      navigationTimer = window.setTimeout(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!navigating) return;
          try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
              destination: destination.pathname + destination.search + destination.hash,
              time: Date.now()
            }));
          } catch (error) {}
          try { window.location.assign(destination.href); } catch (error) { reset(); }
        }));
      }, 1000);
    });
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      reset();
      if (!designMode && !reduceMotion.matches) {
        root.classList.add('header-page-pending');
        revealPage();
      }
    }
  });
  const onMotionChange = () => {
    if (reduceMotion.matches) {
      root.classList.remove('header-page-pending');
      reset();
    }
  };
  if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', onMotionChange);
  else reduceMotion.addListener(onMotionChange);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
